import {useMemo, useState, useCallback, useLayoutEffect} from 'react';
import {useWindowDimensions, Pressable, StyleSheet} from 'react-native';
import {useFloating, autoUpdate, flip} from '@floating-ui/react-dom';
import {PopupContext, PopupOptions} from 'extensions/viewport/PopupContext';
import {Mouse} from 'interface/base/Mouse';

import type {LayoutChangeEvent} from 'react-native';

const CONTENT_REPOSITION_PADDING = 20;
const CONTENT_DEFAULT_WIDTH = 300;
const CONTENT_DEFAULT_HEIGHT = 180;

export const PopupProvider = ({children}) => {
  const screen = useWindowDimensions();
  const [content, setContent] = useState<JSX.Element>();
  const [options, setOptions] = useState<PopupOptions>({});
  const [visible, setVisible] = useState<boolean>(false);
  const [manualPos, setManualPosition] = useState<{x: number, y: number}>();
  const [contentLayout, setContentLayout] = useState<{
    width: number;
    height: number;
  }>();

  const {floating, reference, update, refs, x, y} = useFloating({
    strategy: 'absolute',
    middleware: [
      flip(),
    ],
  });

  const classes = {
    popup: [
      styles.popup,
      options.style,
      {
        left: manualPos ? manualPos.x : x ?? 0,
        top: manualPos ? manualPos.y : y ?? 0,
      },
    ]
  };

  const updateContentLayout = (e: LayoutChangeEvent) => {
    const {width, height} = e.nativeEvent.layout;
    setContentLayout({
      width: width + CONTENT_REPOSITION_PADDING,
      height: height + CONTENT_REPOSITION_PADDING,
    });
  };

  const calculatePosition = (pos: {x: number; y: number}) => {
    const position = {...pos};
    const padding = {
      width: contentLayout?.width ?? CONTENT_DEFAULT_WIDTH,
      height: contentLayout?.height ?? CONTENT_DEFAULT_HEIGHT,
    };
    if (pos.x + padding.width > screen.width)
      position.x = screen.width - padding.width;
    if (pos.y + padding.height > screen.height)
      position.y = screen.height - padding.height;
    setManualPosition(position);
  };

  const context = useMemo(() => ({
    visible,
    reference,
    setRef: (ref: React.RefObject<any>) => (reference(ref.current)),
    setPosition: (pos: {x: number, y: number} | undefined) => {
      if (pos) {
        calculatePosition(pos);
      } else {
        setManualPosition(undefined);
      }
    },
    open: (content: JSX.Element, options?: PopupOptions) => {
      setOptions(options ?? {});
      setContent(content);
      setVisible(true);
    },
    exit: () => {
      setOptions({});
      setContent(undefined);
      setVisible(false);
    },
  }), [content, visible]);

  const noop = useCallback((e) => e.stopPropagation(), []);

  useLayoutEffect(() => {
    if (visible && refs.reference.current && refs.floating.current) {
      autoUpdate(
        refs.reference.current,
        refs.floating.current,
        update,
        {
          ancestorScroll: false,
          animationFrame: true,
        },
      );
    }
  }, [visible]);

  return (
    <PopupContext.Provider value={context}>
      {children}
      {visible &&
        <Pressable style={styles.root} onPressIn={context.exit}>
          <Mouse onContextMenu={context.exit}>
            <Pressable
              ref={floating as any}
              style={classes.popup}
              onLayout={updateContentLayout}
              onPressIn={noop}>
              {content}
            </Pressable>
          </Mouse>
        </Pressable>
      }
    </PopupContext.Provider>
  );
}

const styles = StyleSheet.create({
  root: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99999,
    // @ts-ignore
    position: 'absolute',
    overflow: 'scroll',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitScrollbar: {
      display: 'none',
    },
  },
  popup: {
    position: 'absolute',
  },
});
